import React, { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../components/SEO/SEO";
import Header from "../../components/Header/Header";
import PageTitle from "../../components/pagetitle/PageTitle";
import Footer from "../../components/footer/Footer";
import Scrollbar from "../../components/scrollbar/scrollbar";

import pricingBg from "../../images/bg/pricing-bg.png";
import cardBg from "../../images/bg/pricing-bg01.png";
import subLeftIcon from "../../images/icon/sub-left-icon.png";
import subRightIcon from "../../images/icon/sub-right-icon.png";

// ── Module data with full interconnection map ──
interface ModuleNode {
  slug: string;
  icon: string;
  name: string;
  ai: string;
  color: string;
  connections: { slug: string; label: string }[];
}

const MODULES: ModuleNode[] = [
  {
    slug: "link",
    icon: "fas fa-phone-volume",
    name: "LINK - Business Phone",
    ai: "LINK",
    color: "#00ff97",
    connections: [
      { slug: "crm", label: "Click-to-call from CRM contacts & leads" },
      { slug: "connect", label: "Call summaries post to team channels" },
      { slug: "customer-portal", label: "Customer callback requests trigger LINK calls" },
      { slug: "remedy", label: "Complaint calls logged with ticket auto-creation" },
      { slug: "analytics", label: "Call duration, volume & agent performance metrics" },
    ],
  },
  {
    slug: "chorus",
    icon: "fas fa-share-alt",
    name: "CHORUS - Social Media Studio",
    ai: "CHORUS",
    color: "#8b5cf6",
    connections: [
      { slug: "crm", label: "AI detects buying intent in DMs → auto-creates leads in BARTER" },
      { slug: "remedy", label: "Negative feedback & complaints auto-route to REMEDY tickets" },
      { slug: "storefront", label: "Product catalogue syncs to Instagram Shopping" },
      { slug: "inventory", label: "Shopping orders flow into HAUL inventory" },
      { slug: "accounting", label: "Ad spend tracked in LEDGER expense accounts" },
      { slug: "analytics", label: "Social engagement metrics feed into VARN dashboards" },
    ],
  },
  {
    slug: "connect",
    icon: "fas fa-comments",
    name: "CONNECT - Team Chat & Video",
    ai: "ECHO",
    color: "#3b82f6",
    connections: [
      { slug: "link", label: "Call summaries from LINK post to relevant channels" },
      { slug: "crm", label: "Deal updates & wins notify sales channels" },
      { slug: "projects", label: "FORGE task updates post to project channels" },
      { slug: "hr", label: "HR announcements broadcast to company channels" },
      { slug: "accounting", label: "Payment confirmations appear in finance channels" },
    ],
  },
  {
    slug: "accounting",
    icon: "fas fa-calculator",
    name: "Accounting & Finance",
    ai: "LEDGER",
    color: "#f59e0b",
    connections: [
      { slug: "hr", label: "Payroll processing creates salary journal entries" },
      { slug: "inventory", label: "Purchase orders auto-generate expense entries" },
      { slug: "crm", label: "Sales invoices from deals auto-post to ledger" },
      { slug: "compliance", label: "GST/TDS calculations feed compliance returns" },
      { slug: "upi-payments", label: "UPI collections auto-reconcile with bank entries" },
      { slug: "quotations", label: "Accepted quotes auto-convert to invoices" },
    ],
  },
  {
    slug: "hr",
    icon: "fas fa-users",
    name: "HR & Workforce",
    ai: "KEEPER",
    color: "#ec4899",
    connections: [
      { slug: "accounting", label: "Payroll disbursements journal to LEDGER" },
      { slug: "connect", label: "Onboarding announcements in team channels" },
      { slug: "projects", label: "Employee availability syncs with FORGE assignments" },
      { slug: "compliance", label: "TDS on salaries feeds CIPHER returns" },
    ],
  },
  {
    slug: "inventory",
    icon: "fas fa-boxes",
    name: "Inventory Management",
    ai: "HAUL",
    color: "#14b8a6",
    connections: [
      { slug: "accounting", label: "Stock movements create accounting entries" },
      { slug: "crm", label: "Product availability shown in sales pipeline" },
      { slug: "storefront", label: "Real-time stock levels on public storefront" },
      { slug: "quotations", label: "Quote line items check HAUL stock levels" },
      { slug: "chorus", label: "Instagram Shopping catalogue stays synced" },
    ],
  },
  {
    slug: "costing",
    icon: "fas fa-coins",
    name: "AI Costing Engine",
    ai: "FORGE",
    color: "#f97316",
    connections: [
      { slug: "quotations", label: "BOQ estimates auto-populate quote line items" },
      { slug: "inventory", label: "Component lists check current stock levels" },
      { slug: "accounting", label: "Cost estimates feed project budgets in LEDGER" },
      { slug: "projects", label: "Estimated BOQ becomes project scope in FORGE" },
    ],
  },
  {
    slug: "crm",
    icon: "fas fa-handshake",
    name: "CRM & Sales Pipeline",
    ai: "BARTER",
    color: "#06b6d4",
    connections: [
      { slug: "link", label: "Click-to-call any contact or lead from CRM" },
      { slug: "chorus", label: "Social media leads auto-created from DMs" },
      { slug: "email", label: "Email conversations linked to lead/deal timeline" },
      { slug: "quotations", label: "One-click quote generation from opportunity" },
      { slug: "seal", label: "Send proposals for e-signature via SEAL" },
      { slug: "accounting", label: "Won deals auto-create invoices in LEDGER" },
    ],
  },
  {
    slug: "projects",
    icon: "fas fa-project-diagram",
    name: "Project Management",
    ai: "FORGE",
    color: "#a855f7",
    connections: [
      { slug: "connect", label: "Task updates post to project channels" },
      { slug: "hr", label: "Team member availability from KEEPER" },
      { slug: "accounting", label: "Time tracking feeds billable hours to LEDGER" },
      { slug: "costing", label: "BOQ estimates become project scope & budget" },
    ],
  },
  {
    slug: "email",
    icon: "fas fa-envelope",
    name: "Email Suite",
    ai: "ECHO",
    color: "#64748b",
    connections: [
      { slug: "crm", label: "Email threads linked to CRM contacts & deals" },
      { slug: "remedy", label: "Support emails auto-create REMEDY tickets" },
      { slug: "seal", label: "E-signature requests sent via email" },
    ],
  },
  {
    slug: "compliance",
    icon: "fas fa-shield-alt",
    name: "GST & Tax Compliance",
    ai: "CIPHER",
    color: "#ef4444",
    connections: [
      { slug: "accounting", label: "LEDGER transactions feed GSTR-1/2/3B returns" },
      { slug: "hr", label: "Salary TDS from KEEPER feeds TDS returns" },
      { slug: "inventory", label: "Stock transfers impact ITC reconciliation" },
    ],
  },
  {
    slug: "analytics",
    icon: "fas fa-chart-line",
    name: "Analytics & Reporting",
    ai: "VARN",
    color: "#22d3ee",
    connections: [
      { slug: "accounting", label: "Financial KPIs and P&L dashboards" },
      { slug: "crm", label: "Sales pipeline velocity & conversion metrics" },
      { slug: "hr", label: "Attendance, attrition & workforce analytics" },
      { slug: "chorus", label: "Social media ROI & engagement trends" },
      { slug: "link", label: "Call analytics — volume, duration, agent performance" },
      { slug: "inventory", label: "Stock turnover, dead stock & reorder analytics" },
    ],
  },
  {
    slug: "storefront",
    icon: "fas fa-globe",
    name: "Website Builder",
    ai: "FORGE",
    color: "#10b981",
    connections: [
      { slug: "inventory", label: "Product listings sync from HAUL stock" },
      { slug: "upi-payments", label: "BharatQR on every checkout page" },
      { slug: "crm", label: "Storefront inquiries create CRM leads" },
      { slug: "chorus", label: "Instagram Shopping catalogue sync" },
    ],
  },
  {
    slug: "quotations",
    icon: "fas fa-file-invoice",
    name: "Quotations & Proposals",
    ai: "BARTER",
    color: "#6366f1",
    connections: [
      { slug: "crm", label: "Generate quotes from CRM opportunities" },
      { slug: "seal", label: "Send for e-signature → auto-convert to order" },
      { slug: "accounting", label: "Accepted quotes become invoices in LEDGER" },
      { slug: "costing", label: "AI Costing BOQ auto-populates line items" },
      { slug: "inventory", label: "Line items validated against HAUL stock" },
    ],
  },
  {
    slug: "customer-portal",
    icon: "fas fa-user-circle",
    name: "Customer Portal",
    ai: "ECHO",
    color: "#0ea5e9",
    connections: [
      { slug: "accounting", label: "Customers view invoices & payment history" },
      { slug: "remedy", label: "Submit & track complaints from portal" },
      { slug: "upi-payments", label: "Pay invoices via UPI QR from portal" },
      { slug: "link", label: "Request callbacks that trigger LINK calls" },
    ],
  },
  {
    slug: "remedy",
    icon: "fas fa-headset",
    name: "REMEDY - Complaints",
    ai: "REMEDY",
    color: "#f43f5e",
    connections: [
      { slug: "chorus", label: "Social media complaints auto-create tickets" },
      { slug: "email", label: "Support emails auto-create tickets" },
      { slug: "customer-portal", label: "Portal complaints with SLA tracking" },
      { slug: "link", label: "Phone complaints logged with call recording" },
      { slug: "connect", label: "Escalations notify team via CONNECT channels" },
    ],
  },
  {
    slug: "seal",
    icon: "fas fa-stamp",
    name: "SEAL - E-Signature",
    ai: "SEAL",
    color: "#84cc16",
    connections: [
      { slug: "crm", label: "Proposals sent for signing from CRM deals" },
      { slug: "quotations", label: "Signed quotes auto-convert to orders" },
      { slug: "email", label: "Signing requests delivered via email" },
      { slug: "accounting", label: "Signed contracts trigger invoice creation" },
    ],
  },
  {
    slug: "tender",
    icon: "fas fa-file-contract",
    name: "Tender Management",
    ai: "PROBE",
    color: "#d97706",
    connections: [
      { slug: "costing", label: "AI Costing generates BOQ for bid preparation" },
      { slug: "accounting", label: "EMD/SD tracking in LEDGER" },
      { slug: "projects", label: "Won tenders become FORGE projects" },
      { slug: "compliance", label: "Tender compliance documents tracked by CIPHER" },
    ],
  },
  {
    slug: "upi-payments",
    icon: "fas fa-qrcode",
    name: "UPI Payment Collection",
    ai: "LEDGER",
    color: "#7c3aed",
    connections: [
      { slug: "accounting", label: "UPI collections auto-reconcile with bank entries" },
      { slug: "storefront", label: "BharatQR on every checkout & invoice page" },
      { slug: "customer-portal", label: "Customers pay invoices via QR from portal" },
      { slug: "crm", label: "Payment confirmations update deal status" },
    ],
  },
  {
    slug: "barter",
    icon: "fas fa-store",
    name: "Barter Marketplace",
    ai: "BARTER",
    color: "#e11d48",
    connections: [
      { slug: "inventory", label: "Listed items deducted from HAUL stock" },
      { slug: "crm", label: "Marketplace inquiries create CRM leads" },
      { slug: "accounting", label: "Barter transactions recorded in LEDGER" },
    ],
  },
];

// ── Premium Official Vector SVG Icons for Google Services ──
const GoogleCalendarIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4" width="18" height="18" rx="3" fill="#FFFFFF" />
    <path d="M3 8.5H21V5C21 3.89543 20.1046 3 19 3H5C3.89543 3 3 3.89543 3 5V8.5Z" fill="#4285F4" />
    <path d="M7 1.5V4.5M17 1.5V4.5" stroke="#1A73E8" strokeWidth="2" strokeLinecap="round" />
    <text x="12" y="17" textAnchor="middle" fill="#1A73E8" fontSize="8" fontWeight="bold" fontFamily="sans-serif">31</text>
  </svg>
);

const GoogleDriveIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 87.3 78" fill="none">
    <path d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8H0c0 1.55.4 3.1 1.2 4.5z" fill="#0066DA" />
    <path d="M43.65 25L29.9 1.2c-1.35.8-2.5 1.9-3.3 3.3l-25.4 44A9.06 9.06 0 000 53h27.5z" fill="#00AC47" />
    <path d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5H59.8l5.85 10.15z" fill="#EA4335" />
    <path d="M43.65 25L57.4 1.2C56.05.4 54.5 0 52.9 0H34.4c-1.6 0-3.15.45-4.5 1.2z" fill="#00832D" />
    <path d="M59.8 53H27.5L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684FC" />
    <path d="M73.4 26.5l-12.7-22c-.8-1.4-1.95-2.5-3.3-3.3L43.65 25l16.15 28H87.3c0-1.55-.4-3.1-1.2-4.5z" fill="#FFBA00" />
  </svg>
);

const GoogleSheetsIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <path d="M118 16H46c-7.7 0-14 6.3-14 14v132c0 7.7 6.3 14 14 14h100c7.7 0 14-6.3 14-14V62L118 16z" fill="#0F9D58" />
    <path d="M118 16v46h46L118 16z" fill="#87CEAC" />
    <path d="M58 84h76v72H58V84z" fill="#FFFFFF" />
    <path d="M58 84v72h76V84H58zm22 14v14H64V98h16zm0 20v14H64v-14h16zm0 20v14H64v-14h16zm24-40v14H86V98h16zm0 20v14H86v-14h16zm0 20v14H86v-14h16zm26-40v14h-20V98h20zm0 20v14h-20v-14h20zm0 20v14h-20v-14h20z" fill="#0F9D58" />
  </svg>
);

const GoogleDocsIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <path d="M118 16H46c-7.7 0-14 6.3-14 14v132c0 7.7 6.3 14 14 14h100c7.7 0 14-6.3 14-14V62L118 16z" fill="#4285F4" />
    <path d="M118 16v46h46L118 16z" fill="#A1C2FA" />
    <rect x="56" y="90" width="80" height="10" rx="5" fill="#FFFFFF" />
    <rect x="56" y="112" width="80" height="10" rx="5" fill="#FFFFFF" />
    <rect x="56" y="134" width="52" height="10" rx="5" fill="#FFFFFF" />
  </svg>
);

const GoogleAdsIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <path d="M42.2 135.8c-17.7-30.7-7.2-70 23.5-87.7s70-7.2 87.7 23.5l-23.5 13.6c-10.2-17.7-32.9-23.8-50.6-13.6-17.7 10.2-23.8 32.9-13.6 50.6l-23.5 13.6z" fill="#FBBC04" />
    <path d="M129.9 71.6l23.5-13.6c17.7 30.7 7.2 70-23.5 87.7s-70 7.2-87.7-23.5l23.5-13.6c10.2 17.7 32.9 23.8 50.6 13.6 17.7-10.2 23.8-32.9 13.6-50.6z" fill="#4285F4" />
    <circle cx="56" cy="144" r="22" fill="#34A853" />
  </svg>
);

const GoogleAnalyticsIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <rect x="136" y="32" width="28" height="128" rx="8" fill="#F9AB00" />
    <rect x="82" y="80" width="28" height="80" rx="8" fill="#E37400" />
    <circle cx="42" cy="146" r="14" fill="#F9AB00" />
  </svg>
);

const YouTubeIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <path d="M174.5 56.4c-2-7.6-8-13.6-15.6-15.6C145.1 37.3 96 37.3 96 37.3s-49.1 0-62.9 3.5c-7.6 2-13.6 8-15.6 15.6C14 70.2 14 96 14 96s0 25.8 3.5 39.6c2 7.6 8 13.6 15.6 15.6 13.8 3.5 62.9 3.5 62.9 3.5s49.1 0 62.9-3.5c7.6-2 13.6-8 15.6-15.6 3.5-13.8 3.5-39.6 3.5-39.6s0-25.8-3.5-39.6z" fill="#FF0000" />
    <path d="M80 120.5l42.4-24.5L80 71.5v49z" fill="#FFFFFF" />
  </svg>
);

const GoogleBusinessIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <path d="M96 24L32 60v16h128V60L96 24z" fill="#4285F4" />
    <path d="M32 76h25.6v32c0 7-5.7 12.8-12.8 12.8S32 115 32 108V76z" fill="#4285F4" />
    <path d="M57.6 76h25.6v32c0 7-5.7 12.8-12.8 12.8S57.6 115 57.6 108V76z" fill="#1A73E8" />
    <path d="M83.2 76h25.6v32c0 7-5.7 12.8-12.8 12.8S83.2 115 83.2 108V76z" fill="#4285F4" />
    <path d="M108.8 76h25.6v32c0 7-5.7 12.8-12.8 12.8s-12.8-5.8-12.8-12.8V76z" fill="#1A73E8" />
    <path d="M134.4 76H160v32c0 7-5.7 12.8-12.8 12.8s-12.8-5.8-12.8-12.8V76z" fill="#4285F4" />
    <path d="M44 120h104v44H44v-44z" fill="#E8F0FE" />
    <path d="M96 128c-10 0-18 8-18 18s8 18 18 18 18-8 18-18-8-18-18-18z" fill="#4285F4" />
  </svg>
);

const GoogleClassroomIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 192 192" fill="none">
    <rect x="24" y="32" width="144" height="128" rx="16" fill="#F4B400" />
    <rect x="36" y="44" width="120" height="104" rx="10" fill="#0F9D58" />
    <circle cx="96" cy="80" r="16" fill="#FFFFFF" />
    <path d="M72 124c0-13.3 10.7-24 24-24s24 10.7 24 24H72z" fill="#FFFFFF" />
    <circle cx="60" cy="88" r="11" fill="rgba(255,255,255,0.7)" />
    <path d="M44 124c0-8.8 7.2-16 16-16 4.2 0 8 1.6 10.9 4.3C67.7 116.1 66 120 66 124H44z" fill="rgba(255,255,255,0.7)" />
    <circle cx="132" cy="88" r="11" fill="rgba(255,255,255,0.7)" />
    <path d="M148 124c0-8.8-7.2-16-16-16-4.2 0-8 1.6-10.9 4.3 3.2 3.8 4.9 7.7 4.9 11.7h22z" fill="rgba(255,255,255,0.7)" />
  </svg>
);

interface GoogleLink {
  name: string;
  into: string;
  iconComponent: React.FC;
}

// Google surfaces that land inside an AXOIX module rather than sitting beside it
const GOOGLE_LINKS: GoogleLink[] = [
  { name: "Calendar", into: "→ Bookings", iconComponent: GoogleCalendarIcon },
  { name: "Drive", into: "→ Any record", iconComponent: GoogleDriveIcon },
  { name: "Sheets", into: "→ Exports", iconComponent: GoogleSheetsIcon },
  { name: "Docs", into: "→ Proposals", iconComponent: GoogleDocsIcon },
  { name: "Google Ads", into: "→ Marketing", iconComponent: GoogleAdsIcon },
  { name: "Analytics 4", into: "→ VARN", iconComponent: GoogleAnalyticsIcon },
  { name: "YouTube", into: "→ CHORUS", iconComponent: YouTubeIcon },
  { name: "Business Profile", into: "→ CHORUS", iconComponent: GoogleBusinessIcon },
  { name: "Classroom", into: "→ Education", iconComponent: GoogleClassroomIcon },
];

// ── Example data flows ──
const DATA_FLOWS = [
  {
    title: "Social Media Lead → Closed Deal",
    subtitle: "A customer DMs on Instagram asking about pricing",
    steps: [
      { icon: "fas fa-share-alt", name: "CHORUS" },
      { icon: "fas fa-handshake", name: "BARTER CRM" },
      { icon: "fas fa-phone-volume", name: "LINK Call" },
      { icon: "fas fa-file-invoice", name: "Quote" },
      { icon: "fas fa-stamp", name: "SEAL E-Sign" },
      { icon: "fas fa-calculator", name: "LEDGER Invoice" },
      { icon: "fas fa-qrcode", name: "UPI Payment" },
    ],
  },
  {
    title: "Blueprint → Project Delivery",
    subtitle: "Client uploads a wiring diagram for estimation",
    steps: [
      { icon: "fas fa-coins", name: "AI Costing" },
      { icon: "fas fa-file-invoice", name: "Quote + BOQ" },
      { icon: "fas fa-stamp", name: "SEAL E-Sign" },
      { icon: "fas fa-project-diagram", name: "FORGE Project" },
      { icon: "fas fa-boxes", name: "HAUL Materials" },
      { icon: "fas fa-calculator", name: "LEDGER Billing" },
    ],
  },
  {
    title: "Employee Onboarding → Payroll",
    subtitle: "New hire joins and receives first salary",
    steps: [
      { icon: "fas fa-users", name: "KEEPER HR" },
      { icon: "fas fa-comments", name: "CONNECT Announce" },
      { icon: "fas fa-project-diagram", name: "FORGE Tasks" },
      { icon: "fas fa-phone-volume", name: "LINK Extension" },
      { icon: "fas fa-calculator", name: "LEDGER Payroll" },
      { icon: "fas fa-shield-alt", name: "CIPHER TDS" },
    ],
  },
  {
    title: "Customer Complaint → Resolution",
    subtitle: "A customer posts a negative review on social media",
    steps: [
      { icon: "fas fa-share-alt", name: "CHORUS Detects" },
      { icon: "fas fa-headset", name: "REMEDY Ticket" },
      { icon: "fas fa-comments", name: "CONNECT Alert" },
      { icon: "fas fa-phone-volume", name: "LINK Callback" },
      { icon: "fas fa-headset", name: "REMEDY Resolved" },
      { icon: "fas fa-chart-line", name: "VARN Analytics" },
    ],
  },
];

// ── AXOIX Brand Cyber-Space Styles ──
const pageStyles = `
  .ic-hero-stats-wrap {
    background: rgba(10, 22, 45, 0.65);
    border: 1px solid rgba(0, 255, 151, 0.2);
    border-radius: 20px;
    padding: 30px;
    backdrop-filter: blur(12px);
    margin-top: 30px;
  }
  .ic-stats {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    align-items: stretch;
  }
  @media (max-width: 991px) {
    .ic-stats { grid-template-columns: repeat(2, 1fr); }
  }
  @media (max-width: 575px) {
    .ic-stats { grid-template-columns: 1fr; }
  }
  .ic-stat {
    text-align: center;
    background: rgba(0, 255, 151, 0.04);
    border: 1px solid rgba(0, 255, 151, 0.12);
    border-radius: 14px;
    padding: 24px 15px;
    transition: all 0.3s ease;
    height: 100%;
    min-height: 130px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
  .ic-stat:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.15);
  }
  .ic-stat-num {
    font-size: 2.4rem;
    font-weight: 800;
    color: var(--color-primary);
    line-height: 1;
    margin-bottom: 8px;
    letter-spacing: -0.5px;
  }
  .ic-stat-label {
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.7);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    min-height: 38px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }

  /* Module selector grid */
  .ic-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 16px;
    margin-bottom: 50px;
    align-items: stretch;
  }
  @media (max-width: 1199px) { .ic-grid { grid-template-columns: repeat(4, 1fr); } }
  @media (max-width: 991px) { .ic-grid { grid-template-columns: repeat(3, 1fr); } }
  @media (max-width: 575px) { .ic-grid { grid-template-columns: repeat(2, 1fr); } }

  .ic-node {
    background: rgba(12, 24, 50, 0.65);
    border: 1px solid rgba(0, 255, 151, 0.15);
    border-radius: 16px;
    padding: 24px 14px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
    position: relative;
    backdrop-filter: blur(10px);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    height: 100%;
    min-height: 190px;
  }
  .ic-node:hover {
    border-color: var(--color-primary);
    background: rgba(0, 255, 151, 0.08);
    transform: translateY(-4px);
    box-shadow: 0 10px 30px rgba(0, 255, 151, 0.15);
  }
  .ic-node--active {
    border-color: var(--color-primary) !important;
    background: rgba(0, 255, 151, 0.14) !important;
    box-shadow: 0 0 25px rgba(0, 255, 151, 0.25) !important;
    transform: translateY(-2px);
  }
  .ic-node--connected {
    border-color: rgba(0, 255, 151, 0.45);
    background: rgba(0, 255, 151, 0.06);
    box-shadow: 0 0 16px rgba(0, 255, 151, 0.1);
  }
  .ic-node-icon {
    width: 52px;
    height: 52px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.15), rgba(0, 255, 151, 0.04));
    border: 1px solid rgba(0, 255, 151, 0.25);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    font-size: 20px;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }
  .ic-node:hover .ic-node-icon,
  .ic-node--active .ic-node-icon {
    background: var(--color-primary);
    color: #060b18;
    transform: scale(1.06);
  }
  .ic-node-name {
    font-size: 0.88rem;
    font-weight: 600;
    color: var(--color-white);
    line-height: 1.35;
    margin-bottom: 8px;
    min-height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    width: 100%;
  }
  .ic-node-ai {
    font-size: 0.72rem;
    color: rgba(0, 255, 151, 0.85);
    font-weight: 500;
    margin-top: auto;
    display: block;
    text-align: center;
    width: 100%;
    padding-top: 4px;
  }
  .ic-node-count {
    position: absolute;
    top: 10px;
    right: 10px;
    background: var(--color-primary);
    color: #060b18;
    font-size: 10px;
    font-weight: 700;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(0, 255, 151, 0.4);
  }

  /* Detail panel */
  .ic-detail {
    background: rgba(12, 24, 50, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 35px 40px;
    margin-bottom: 50px;
    position: relative;
    overflow: hidden;
    height: auto !important;
    min-height: unset !important;
    backdrop-filter: blur(16px);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
  }
  @media (max-width: 767px) {
    .ic-detail { padding: 25px 20px; }
  }
  .ic-detail-header {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 30px;
    flex-wrap: wrap;
    padding-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  }
  .ic-detail-icon {
    width: 64px;
    height: 64px;
    border-radius: 16px;
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.2), rgba(0, 255, 151, 0.05));
    border: 1px solid rgba(0, 255, 151, 0.35);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 26px;
    flex-shrink: 0;
  }
  .ic-detail-title {
    font-size: 1.6rem;
    font-weight: 700;
    color: var(--color-white);
    margin: 0 0 6px;
  }
  .ic-detail-ai {
    display: inline-block;
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 16px;
    padding: 3px 12px;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--color-primary);
  }
  .ic-detail-subtitle {
    font-size: 1.15rem;
    font-weight: 600;
    color: var(--color-white);
    margin-bottom: 20px;
  }

  /* Connection cards */
  .ic-conn-list {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    align-items: stretch;
  }
  @media (max-width: 767px) { .ic-conn-list { grid-template-columns: 1fr; } }

  .ic-conn-card {
    display: flex;
    align-items: center;
    gap: 16px;
    background: rgba(10, 20, 42, 0.7);
    border: 1px solid rgba(0, 255, 151, 0.15);
    border-radius: 14px;
    padding: 18px 20px;
    text-decoration: none;
    color: inherit;
    transition: all 0.25s ease;
    backdrop-filter: blur(8px);
    height: 100%;
    min-height: 96px;
  }
  .ic-conn-card:hover {
    border-color: var(--color-primary);
    background: rgba(0, 255, 151, 0.08);
    color: inherit;
    text-decoration: none;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 151, 0.12);
  }
  .ic-conn-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.15), rgba(0, 255, 151, 0.03));
    border: 1px solid rgba(0, 255, 151, 0.25);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    transition: all 0.2s ease;
  }
  .ic-conn-card:hover .ic-conn-icon {
    background: var(--color-primary);
    color: #060b18;
  }
  .ic-conn-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-white);
    margin-bottom: 4px;
  }
  .ic-conn-label {
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.45;
    margin: 0;
  }
  .ic-conn-arrow {
    font-size: 0.8rem;
    color: var(--color-primary);
    margin-left: auto;
    flex-shrink: 0;
    transition: transform 0.2s ease;
  }
  .ic-conn-card:hover .ic-conn-arrow {
    transform: translateX(4px);
  }

  /* Prompt text */
  .ic-prompt {
    text-align: center;
    padding: 50px 20px;
    background: rgba(12, 24, 50, 0.4);
    border: 1px dashed rgba(0, 255, 151, 0.25);
    border-radius: 18px;
    color: rgba(255, 255, 255, 0.6);
    font-size: 1.05rem;
    margin-bottom: 50px;
  }
  .ic-prompt i {
    display: block;
    font-size: 2.2rem;
    margin-bottom: 14px;
    color: var(--color-primary);
    opacity: 0.75;
  }

  /* Flow diagram section */
  .ic-flow-card {
    background: rgba(12, 24, 50, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.2);
    border-radius: 20px;
    padding: 30px 35px;
    margin-bottom: 24px;
    height: auto !important;
    min-height: unset !important;
    backdrop-filter: blur(16px);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
    text-align: center;
  }
  @media (max-width: 767px) {
    .ic-flow-card { padding: 24px 16px; }
  }
  .ic-flow-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-white);
    margin-bottom: 6px;
    text-align: center;
  }
  .ic-flow-subtitle {
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 22px;
    text-align: center;
  }
  .ic-flow-steps {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    row-gap: 16px;
    flex-wrap: wrap;
  }
  .ic-flow-step {
    background: rgba(10, 22, 45, 0.85);
    border: 1px solid rgba(0, 255, 151, 0.2);
    border-radius: 12px;
    padding: 12px 16px;
    text-align: center;
    min-width: 120px;
    height: 92px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    transition: all 0.25s ease;
    flex-shrink: 0;
  }
  .ic-flow-step:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 255, 151, 0.15);
  }
  .ic-flow-step-icon {
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.15), rgba(0, 255, 151, 0.03));
    border: 1px solid rgba(0, 255, 151, 0.25);
    color: var(--color-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    margin: 0 auto 6px;
    flex-shrink: 0;
  }
  .ic-flow-step-name {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--color-white);
    min-height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.25;
  }
  .ic-flow-arrow {
    font-size: 0.95rem;
    color: var(--color-primary);
    padding: 0 4px;
    opacity: 0.75;
    flex-shrink: 0;
  }
  @media (max-width: 767px) {
    .ic-flow-arrow { transform: rotate(90deg); padding: 6px 0; }
    .ic-flow-steps { flex-direction: column; width: 100%; align-items: stretch; }
    .ic-flow-step { width: 100%; }
  }

  /* Outside connections */
  .ic-ext-card {
    background: rgba(12, 24, 50, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 40px;
    height: auto !important;
    min-height: unset !important;
    backdrop-filter: blur(16px);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
  }
  @media (max-width: 767px) {
    .ic-ext-card { padding: 25px 18px; }
  }
  .ic-ext-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-top: 30px;
    align-items: stretch;
  }
  @media (max-width: 991px) { .ic-ext-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 575px) { .ic-ext-grid { grid-template-columns: 1fr; } }
  .ic-ext-item {
    background: rgba(10, 20, 42, 0.7);
    border: 1px solid rgba(0, 255, 151, 0.15);
    border-radius: 14px;
    padding: 18px 16px;
    display: flex;
    align-items: center;
    gap: 16px;
    transition: all 0.3s ease;
    height: 100%;
    min-height: 84px;
  }
  .ic-ext-item:hover {
    border-color: var(--color-primary);
    background: rgba(0, 255, 151, 0.06);
    transform: translateY(-3px);
    box-shadow: 0 8px 25px rgba(0, 255, 151, 0.12);
  }
  .ic-ext-logo {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .ic-ext-name {
    font-size: 0.95rem;
    font-weight: 600;
    color: var(--color-white);
    margin-bottom: 4px;
  }
  .ic-ext-into {
    display: inline-block;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--color-primary);
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 12px;
    padding: 2px 8px;
  }

  /* Bottom CTA */
  .ic-cta-card {
    background: rgba(12, 24, 50, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 50px 40px;
    height: auto !important;
    min-height: unset !important;
    backdrop-filter: blur(16px);
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    position: relative;
  }
`;

// ── Component ──
const InterconnectionsPage: React.FC = () => {
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  useEffect(() => {
    document.title = "How Modules Connect — AXOIX | 20 Interconnected Modules";
  }, []);

  const selectedModule = MODULES.find((m) => m.slug === selectedSlug);
  const connectedSlugs = selectedModule
    ? selectedModule.connections.map((c) => c.slug)
    : [];

  // Count total unique connections
  const totalConnections = MODULES.reduce((sum, m) => sum + m.connections.length, 0);

  const getModule = (slug: string) => MODULES.find((m) => m.slug === slug);

  return (
    <Fragment>
      <SEO
        title="How AXOIX Modules Connect"
        description="See how the 18 AXOIX modules pass data to each other — orders into accounting, calls into CRM, attendance into payroll — so nothing is re-keyed between systems."
        path="/how-it-connects"
      />
      <div className="interconnections-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main
            style={{
              backgroundImage: `url(${pricingBg})`,
              backgroundSize: "cover",
              backgroundPosition: "center top",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "fixed",
            }}
          >
            <PageTitle pageTitle="How Modules Connect" pagesub="Interconnections" />
            <style>{pageStyles}</style>

            {/* Hero Stats */}
            <section className="pt-80 pb-40">
              <div className="container">
                <div className="sec-title sec-title--two text-center mb-50">
                  <span className="sub-title mb-20">
                    <img src={subLeftIcon} alt="" /> Unified Business Architecture <img src={subRightIcon} alt="" />
                  </span>
                  <h2 className="title">20 Modules. Seamlessly Interconnected.</h2>
                  <p
                    className="opacity-75 mt-20"
                    style={{ maxWidth: 720, margin: "20px auto 0", fontSize: "1.05rem", lineHeight: 1.7 }}
                  >
                    Every AXOIX module talks to every other. CHORUS detects a lead and
                    BARTER CRM picks it up. LINK logs a call and CONNECT notifies the team.
                    LEDGER journals a payroll entry from KEEPER. Click any module below to
                    explore its live data pathways.
                  </p>
                </div>

                <div className="ic-hero-stats-wrap">
                  <div className="ic-stats">
                    <div className="ic-stat">
                      <div className="ic-stat-num">20</div>
                      <div className="ic-stat-label">Business Modules</div>
                    </div>
                    <div className="ic-stat">
                      <div className="ic-stat-num">{totalConnections}</div>
                      <div className="ic-stat-label">Data Connections</div>
                    </div>
                    <div className="ic-stat">
                      <div className="ic-stat-num">22</div>
                      <div className="ic-stat-label">AI Characters</div>
                    </div>
                    <div className="ic-stat">
                      <div className="ic-stat-num">0</div>
                      <div className="ic-stat-label">Manual Copy-Paste</div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Interactive Module Selector */}
            <section className="pt-0 pb-60">
              <div className="container">
                <div className="sec-title sec-title--two text-center mb-40">
                  <span className="sub-title mb-20">
                    <img src={subLeftIcon} alt="" /> Interactive Network Map <img src={subRightIcon} alt="" />
                  </span>
                  <h3 className="title" style={{ fontSize: "1.8rem" }}>Select a Module to Inspect Its Bridges</h3>
                </div>

                <div className="ic-grid">
                  {MODULES.map((mod) => {
                    const isActive = selectedSlug === mod.slug;
                    const isConnected = connectedSlugs.includes(mod.slug);
                    return (
                      <div
                        key={mod.slug}
                        className={`ic-node ${isActive ? "ic-node--active" : ""} ${isConnected ? "ic-node--connected" : ""}`}
                        onClick={() => setSelectedSlug(isActive ? null : mod.slug)}
                      >
                        <span className="ic-node-count">{mod.connections.length}</span>
                        <div className="ic-node-icon">
                          <i className={mod.icon}></i>
                        </div>
                        <div className="ic-node-name">{mod.name}</div>
                        <div className="ic-node-ai">Powered by {mod.ai}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Detail Panel */}
                {selectedModule ? (
                  <div
                    className="ic-detail xb-border"
                    style={{ backgroundImage: `url(${cardBg})` }}
                  >
                    <div className="ic-detail-header">
                      <div className="ic-detail-icon">
                        <i className={selectedModule.icon}></i>
                      </div>
                      <div>
                        <h3 className="ic-detail-title">{selectedModule.name}</h3>
                        <span className="ic-detail-ai">
                          Powered by {selectedModule.ai} &middot;{" "}
                          {selectedModule.connections.length} live data bridges
                        </span>
                      </div>
                      <Link
                        to={`/feature/${selectedModule.slug}`}
                        className="thm-btn agency-btn ms-auto"
                        style={{ fontSize: "0.85rem", padding: "10px 24px" }}
                      >
                        <span className="text">View Full Module</span>
                      </Link>
                    </div>

                    <div className="ic-detail-subtitle">
                      Automated bridges with {selectedModule.connections.length} modules:
                    </div>

                    <div className="ic-conn-list">
                      {selectedModule.connections.map((conn, idx) => {
                        const target = getModule(conn.slug);
                        if (!target) return null;
                        return (
                          <Link
                            key={idx}
                            to={`/feature/${conn.slug}`}
                            className="ic-conn-card"
                          >
                            <div className="ic-conn-icon">
                              <i className={target.icon}></i>
                            </div>
                            <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                              <div className="ic-conn-name">{target.name}</div>
                              <p className="ic-conn-label">{conn.label}</p>
                            </div>
                            <span className="ic-conn-arrow">
                              <i className="fas fa-chevron-right"></i>
                            </span>
                          </Link>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="ic-prompt">
                    <i className="fas fa-network-wired"></i>
                    Click any module above to inspect its real-time data connections
                  </div>
                )}
              </div>
            </section>

            {/* Data Flow Examples */}
            <section className="pt-20 pb-60">
              <div className="container">
                <div className="sec-title sec-title--two text-center mb-50">
                  <span className="sub-title mb-20">
                    <img src={subLeftIcon} alt="" /> Real-World Data Pipelines <img src={subRightIcon} alt="" />
                  </span>
                  <h2 className="title">See How Data Moves Across Modules</h2>
                  <p
                    className="opacity-75 mt-20"
                    style={{ maxWidth: 640, margin: "20px auto 0", fontSize: "1.05rem" }}
                  >
                    These aren't theoretical — these are actual automated workflows running in
                    production AXOIX tenant dashboards today.
                  </p>
                </div>

                <div className="row justify-content-center">
                  <div className="col-12 d-flex flex-column gap-4">
                    {DATA_FLOWS.map((flow, fi) => (
                      <div
                        key={fi}
                        className="ic-flow-card xb-border"
                        style={{ backgroundImage: `url(${cardBg})` }}
                      >
                        <div className="ic-flow-title">{flow.title}</div>
                        <div className="ic-flow-subtitle">{flow.subtitle}</div>
                        <div className="ic-flow-steps">
                          {flow.steps.map((step, si) => (
                            <React.Fragment key={si}>
                              {si > 0 && (
                                <span className="ic-flow-arrow">
                                  <i className="fas fa-arrow-right"></i>
                                </span>
                              )}
                              <div className="ic-flow-step">
                                <div className="ic-flow-step-icon">
                                  <i className={step.icon}></i>
                                </div>
                                <div className="ic-flow-step-name">{step.name}</div>
                              </div>
                            </React.Fragment>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </section>

            {/* Connections that leave AXOIX */}
            <section className="pt-20 pb-80">
              <div className="container">
                <div
                  className="ic-ext-card xb-border"
                  style={{ backgroundImage: `url(${cardBg})` }}
                >
                  <div className="sec-title sec-title--two text-center mb-30">
                    <span className="sub-title mb-20">
                      <img src={subLeftIcon} alt="" /> Ecosystem Integration <img src={subRightIcon} alt="" />
                    </span>
                    <h2 className="title">It Connects Outward Too</h2>
                    <p
                      className="opacity-75 mt-20"
                      style={{ maxWidth: 660, margin: "20px auto 0", fontSize: "1.05rem" }}
                    >
                      The modules above pass data to each other. They also reach the tools you
                      already use — most substantially Google, where 9 services land directly
                      inside the AXOIX screen that needs them rather than in a separate tab.
                    </p>
                  </div>

                  <div className="ic-ext-grid">
                    {GOOGLE_LINKS.map((g) => {
                      const IconComponent = g.iconComponent;
                      return (
                        <div className="ic-ext-item" key={g.name}>
                          <div className="ic-ext-logo">
                            <IconComponent />
                          </div>
                          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                            <div className="ic-ext-name">{g.name}</div>
                            <div>
                              <span className="ic-ext-into">{g.into}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="text-center mt-40">
                    <Link to="/feature/google-workspace" className="thm-btn agency-btn">
                      <span className="text">See the Google modules</span>
                    </Link>
                  </div>
                </div>
              </div>
            </section>

            {/* CTA */}
            <section className="pt-0 pb-100">
              <div className="container">
                <div
                  className="ic-cta-card xb-border text-center"
                  style={{ backgroundImage: `url(${cardBg})` }}
                >
                  <h3
                    style={{
                      color: "var(--color-white)",
                      fontWeight: 700,
                      fontSize: "2rem",
                      marginBottom: 16,
                    }}
                  >
                    Every module works together. Zero manual effort.
                  </h3>
                  <p
                    className="opacity-75 mb-35"
                    style={{ maxWidth: 580, margin: "0 auto 30px", fontSize: "1.05rem", lineHeight: 1.6 }}
                  >
                    Stop copy-pasting between disconnected tools. AXOIX modules share
                    data automatically so you can focus on running your business.
                  </p>
                  <div className="d-flex gap-3 justify-content-center flex-wrap">
                    <Link to="/features" className="thm-btn agency-btn">
                      <span className="text">Explore All Modules</span>
                    </Link>
                    <Link
                      to="/pricing"
                      className="thm-btn agency-btn"
                      style={{
                        background: "transparent",
                        border: "1px solid var(--color-primary)",
                      }}
                    >
                      <span className="text">View Pricing</span>
                    </Link>
                  </div>
                </div>
              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default InterconnectionsPage;
